import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, CreditCard, Sparkles, Info } from 'lucide-react';
import Tooltip from '@mui/material/Tooltip';
import { CustomMuiTelInput } from '../CustomMuiTelInput';
import { matchIsValidTel } from 'mui-tel-input';
import TradingAccountCard from './TradingAccountCard';

interface FormData {
  prenom: string;
  email: string;
  phone: string;
  budget: string;
}

interface FormErrors {
  prenom?: string;
  email?: string;
  phone?: string;
  budget?: string;
}

const LeadForm: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    prenom: '',
    email: '',
    phone: '',
    budget: '',
  });
  const [telTouched, setTelTouched] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  const steps = [
    'Clique sur le bouton pour ouvrir un compte de trading',
    'Remplis tes informations sur le site du partenaire',
    'Valide ton email et connecte-toi à RaiseFX',
    'Profite de tes modules et alertes en temps réel',
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.prenom.trim()) {
      newErrors.prenom = 'Le prénom est requis';
    }
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const isPhoneValid = matchIsValidTel(formData.phone) && phoneDigits.length >= 11;
    if (!formData.phone.trim()) {
      newErrors.phone = 'Le numéro de téléphone est requis';
    } else if (!isPhoneValid) {
      newErrors.phone = 'Format de numéro invalide';
    }

    const budgetNumber = parseInt(formData.budget, 10);
    if (!formData.budget) {
      newErrors.budget = 'Le capital prévu est requis';
    } else if (isNaN(budgetNumber)) {
      newErrors.budget = 'Format de montant invalide';
    } else if (budgetNumber < 200) {
      newErrors.budget = 'Le dépôt minimum chez RaiseFX est de 200€ pour démarrer le trading, mais nous conseillons 500€ ou plus.';
    } else if (budgetNumber > 100000) {
      newErrors.budget = 'Ce formulaire est destiné aux personnes prêtes à se lancer sérieusement dans le trading. Merci d’indiquer un montant réaliste.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch('https://evoltrade.fr/api/user/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          boardId: 7669473566,
          prenom: formData.prenom,
          nom: '',
          email: formData.email,
          telephone: formData.phone,
          statut: 'Lead',
          newsLetter: true,
          capital: formData.budget,
        }),
      });

      if (response.ok) {
        localStorage.setItem('userEmail', formData.email);
        setIsRegistered(true);
      } else {
        const errText = await response.text();
        setErrors({ budget: `Erreur lors de l'enregistrement: ${errText}` });
      }
    } catch (err) {
      console.error(err);
      setErrors({ budget: 'Erreur lors de la soumission.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isRegistered) {
    return <TradingAccountCard steps={steps} />;
  }

  return (
    <div id="lead-form" className="bg-white rounded-2xl shadow-2xl p-6 lg:p-8">
      <div className="text-center mb-6">
        <h3 className="font-poppins font-bold text-xl lg:text-2xl text-gray-900 mb-2 flex items-center justify-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          Réserve ta place maintenant
        </h3>
        <p className="text-gray-600 text-sm lg:text-base">
          Un membre de notre équipe te contactera afin de te guider au mieux dans tes premiers pas en trading.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Prénom */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="inline w-4 h-4 mr-2" />
            Prénom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.prenom}
            onChange={e => handleInputChange('prenom', e.target.value)}
            className={`w-full px-4 py-3 lg:py-4 border-2 rounded-xl transition-all duration-200 focus:outline-none text-base text-gray-900 ${
              errors.prenom
                ? 'border-red-300 focus:border-red-500'
                : 'border-gray-200 focus:border-purple-500'
            }`}
            placeholder="Ton prénom"
          />
          {errors.prenom && <p className="text-red-500 text-sm mt-1">{errors.prenom}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="inline w-4 h-4 mr-2" />
            Adresse email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={e => handleInputChange('email', e.target.value)}
            className={`w-full px-4 py-3 lg:py-4 border-2 rounded-xl transition-all duration-200 focus:outline-none text-base text-gray-900 ${
              errors.email
                ? 'border-red-300 focus:border-red-500'
                : 'border-gray-200 focus:border-purple-500'
            }`}
            placeholder="ton@email.com"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        {/* Téléphone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="inline w-4 h-4 mr-2" />
            Numéro de téléphone <span className="text-red-500">*</span>
          </label>
          <CustomMuiTelInput
            colorMode="light"
            defaultCountry="FR"
            value={formData.phone}
            onChange={value => {
              setTelTouched(true);
              handleInputChange('phone', value);
            }}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: errors.phone ? '#FCA5A5' : '#e5e7eb',
                borderWidth: '2px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: errors.phone ? '#FCA5A5' : '#e5e7eb',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: errors.phone ? '#FCA5A5' : '#EF4444',
              },
            }}
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>

        {/* Capital prévu */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <CreditCard className="inline w-4 h-4 mr-2" />
            Capital actuel prévu pour le trading  <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.budget}
            onChange={e => handleInputChange('budget', e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="Montant en €"
            className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none text-gray-900 placeholder-gray-400 ${
              errors.budget
                ? 'border-red-300 focus:border-red-500'
                : 'border-gray-200 focus:border-purple-500'
            }`}
          />
          {errors.budget && (
            <p className="text-red-500 text-sm mt-1 flex items-center">
              {errors.budget}
              {formData.budget && parseInt(formData.budget, 10) < 200 && (
                <Tooltip
                  title="Plus votre capital de départ est élevé, meilleur est le money management, les risques sont minimisés et les profits potentiels augmentent."
                  arrow
                >
                  <Info className="w-4 h-4 text-red-500 ml-1 cursor-pointer" />
                </Tooltip>
              )}
            </p>
          )}
        </div>

        {/* CTA */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-poppins font-bold py-4 lg:py-5 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg text-base lg:text-lg"
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Envoi en cours...
            </div>
          ) : (
            <span>Débloquer mon accès privé au Discord</span>
          )}
        </button>
        <p className="text-center text-xs text-gray-500">
           Tu créeras ton compte de trading à l’étape suivante, avant de pouvoir rejoindre le Discord.
        </p>

        {/* Footer */}
        <div className="text-center pt-4 border-t border-gray-100">
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center justify-center">
              <span>           
                📞 Merci de remplir tes vraies infos pour qu’on puisse te contacter et t’ajouter au Discord.
              </span>
            </div>            
            <div className="flex items-center justify-center">
              <span className="text-blue-600 mr-2">🔒</span>
              <span>Données protégées, aucun spam</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LeadForm; 
